import { useQueryClient } from '@tanstack/react-query';
import { createElement, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type {
  AuthScheme,
  DeleteIntegrationAuthSchemesUuid200,
  IntegrationValidationError
} from 'api';
import {
  getGetIntegrationAuthSchemesQueryKey,
  getGetIntegrationWebhooksQueryKey,
  useDeleteIntegrationAuthSchemesUuid,
  usePostIntegrationAuthSchemesUuidEnable
} from 'api';

import { useOrganisationId } from 'hooks/context';

import {
  SCHEME_IN_USE_CODE,
  SCHEME_IN_USE_ERROR,
  TOGGLE_SCHEME_ERROR
} from './constants';
import { DependentHooksNotice } from './DependentHooksNotice';
import {
  dependentHookRows,
  failedHooksIntro,
  inactiveHooksIntro,
  toggleSchemeSuccess
} from './utils';

import { useWebhooks } from '../../hooks/useWebhooks';

export const useToggleSchemeStatus = () => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const { webhooks } = useWebhooks();
  const enableMutation = usePostIntegrationAuthSchemesUuidEnable();
  const disableMutation = useDeleteIntegrationAuthSchemesUuid();
  const [blockedError, setBlockedError] = useState<string | null>(null);

  const notifySuccess = (
    disabling: boolean,
    { data }: DeleteIntegrationAuthSchemesUuid200
  ) => {
    const pendingHooks =
      (disabling ? data.failed_hooks : data.inactive_hooks) ?? [];
    if (pendingHooks.length === 0) {
      openNotification({
        kind: 'success',
        message: toggleSchemeSuccess(disabling)
      });
      return;
    }

    const intro = disabling
      ? failedHooksIntro(pendingHooks.length)
      : inactiveHooksIntro(pendingHooks.length);
    openNotification({
      kind: disabling ? 'warning' : 'info',
      message: toggleSchemeSuccess(disabling),
      description: createElement(DependentHooksNotice, {
        intro,
        hooks: dependentHookRows(pendingHooks, webhooks)
      }),
      duration: 0
    });
  };

  const toggle = (scheme: AuthScheme, onSuccess: () => void) => {
    const mutation = scheme.enabled ? disableMutation : enableMutation;
    setBlockedError(null);
    mutation.mutate(
      { organisationId, uuid: scheme.uuid },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries({
            queryKey: getGetIntegrationAuthSchemesQueryKey(organisationId)
          });
          queryClient.invalidateQueries({
            queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
          });
          notifySuccess(scheme.enabled, response);
          onSuccess();
        },
        onError: (error: IntegrationValidationError | null | undefined) => {
          if (error?.error?.code === SCHEME_IN_USE_CODE) {
            setBlockedError(SCHEME_IN_USE_ERROR);
            return;
          }
          openNotification({ kind: 'error', message: TOGGLE_SCHEME_ERROR });
        }
      }
    );
  };

  return {
    toggle,
    blockedError,
    clearBlockedError: () => setBlockedError(null),
    isPending: enableMutation.isPending || disableMutation.isPending
  };
};
